/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Spinner from './Spinner';
import DataTable, { ActionMenu, DataTableColumn } from './DataTable';
import { Icons } from './ActionIcons';
import AppModal from './AppModal';
import AlertConfirmModal from './AlertConfirmModal';
import AppToastContainer, { AppToastItem } from './AppToastContainer';

type JobExecution = {
  _id: string;
  queueName: string;
  jobName: string;
  status: string;
  bullJobId?: string;
  workerPid?: number;
  progress?: number;
  progressMessage?: string;
  attemptsMade?: number;
  maxAttempts?: number;
  createdBy?: { id?: string; name?: string };
  payload?: any;
  result?: any;
  createdAt?: string;
  startedAt?: string;
  finishedAt?: string;
  errorMessage?: string;
  cancelRequested?: boolean;
  lastHeartbeatAt?: string;
  updatedAt?: string;
  runtimeStats?: {
    sampledAt?: string;
    processCpuPct?: number;
    processRssMb?: number;
    processHeapUsedMb?: number;
    processHeapLimitMb?: number;
    processHeapUsedPct?: number;
    hostTotalMemMb?: number;
    hostFreeMemMb?: number;
    hostMemUsedPct?: number;
    hostCpuPct?: number;
    activeHandlers?: number;
    maxActiveHandlersConfigured?: number;
  };
  resourceEstimate?: {
    estimatedCpuPct?: number;
    estimatedRamMb?: number;
    estimatedProcessSharePct?: number;
    method?: string;
  };
};

type OverviewQueue = {
  queueName: string;
  counts: {
    waiting?: number;
    active?: number;
    completed?: number;
    failed?: number;
    delayed?: number;
    paused?: number;
  };
};

type CapacityResponse = {
  note?: string;
  concurrencyConfig?: {
    default?: number;
    sap?: number;
    sapHeavy?: number;
    sapComplete?: number;
    system?: number;
    maxActiveGlobal?: number;
  };
  workers?: Array<{
    workerPid?: number;
    queueName?: string;
    sampledAt?: string;
    runtimeStats?: {
      processCpuPct?: number;
      processRssMb?: number;
      processHeapUsedMb?: number;
      processHeapUsedPct?: number;
      hostMemUsedPct?: number;
      hostCpuPct?: number;
      activeHandlers?: number;
      maxActiveHandlersConfigured?: number;
    };
  }>;
  activeJobs?: JobExecution[];
};

const TERMINAL_JOB_STATUSES = ['completed', 'failed', 'cancelled'] as const;

const resolveApiBase = () => {
  const baseEnv = process.env.REACT_APP_API_URL || '';
  const hostname = window.location.hostname;
  const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';
  if (isLocalHost) return '';
  if (!baseEnv) return '';
  try {
    const envUrl = new URL(baseEnv);
    if (envUrl.origin === window.location.origin) return '';
    return baseEnv.replace(/\/$/, '');
  } catch {
    return baseEnv.replace(/\/$/, '');
  }
};

const buildConsolidationJobDetail = (row: JobExecution) => {
  const jobName = String(row.jobName || '').toLowerCase();
  const payload = row.payload || {};

  const workloadTier = String(payload?.workloadTier || '').trim().toLowerCase();
  const workloadTierLabel = workloadTier === 'heavy'
    ? 'Pesado'
    : workloadTier === 'medium'
      ? 'Médio'
      : workloadTier === 'light'
        ? 'Leve'
        : workloadTier === 'simple'
          ? 'Simples'
          : '';
  const estimatedLogCount = Math.max(0, Number(payload?.estimatedLogCount || 0));

  if (jobName.includes('consolidation-user-code-child')) {
    const username = String(payload?.username || '').trim();
    const code = String(payload?.code || '').trim();
    if (username && code) return `Par: ${username} + ${code}`;
  }

  if (jobName.includes('consolidation-user-child')) {
    const detailParts: string[] = [];
    if (workloadTierLabel) detailParts.push(`Faixa: ${workloadTierLabel}`);
    if (estimatedLogCount > 0) detailParts.push(`~${estimatedLogCount.toLocaleString('pt-BR')} registros`);

    const usernames = Array.isArray(payload?.usernames)
      ? payload.usernames.map((item: unknown) => String(item || '').trim()).filter(Boolean)
      : [];
    if (usernames.length > 0) {
      const maxShown = 5;
      const shown = usernames.slice(0, maxShown).join(', ');
      const suffix = usernames.length > maxShown ? ` +${usernames.length - maxShown}` : '';
      detailParts.push(`Usuários: ${shown}${suffix}`);
      return detailParts.join(' | ');
    }
    const username = String(payload?.username || '').trim();
    if (username) {
      detailParts.push(`Usuário: ${username}`);
      return detailParts.join(' | ');
    }
    if (detailParts.length > 0) return detailParts.join(' | ');
  }

  if (jobName.includes('consolidation-user-code-finalize') || jobName.includes('consolidation-users-finalize')) {
    const pollAttempt = Math.max(0, Number(payload?.pollAttempt || 0));
    const maxPollAttempts = Math.max(0, Number(payload?.maxPollAttempts || 0));
    if (pollAttempt > 0 && maxPollAttempts > 0) {
      return `Finalize poll ${pollAttempt}/${maxPollAttempts}`;
    }
    if (pollAttempt > 0) return `Finalize poll ${pollAttempt}`;
  }

  return '';
};

const JobMonitor: React.FC = () => {
  const [theme] = useState(localStorage.getItem('phoenixTheme') || 'light');
  const [loading, setLoading] = useState(true);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [overviewQueues, setOverviewQueues] = useState<OverviewQueue[]>([]);
  const [capacity, setCapacity] = useState<CapacityResponse | null>(null);
  const [jobs, setJobs] = useState<JobExecution[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState('');
  const [queueFilter, setQueueFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [inFlightOnly, setInFlightOnly] = useState(false);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [toasts, setToasts] = useState<AppToastItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [detailJob, setDetailJob] = useState<JobExecution | null>(null);
  const [dialogState, setDialogState] = useState<{
    show: boolean;
    title: string;
    message: string;
    confirmLabel?: string;
    confirmVariant?: 'primary' | 'danger' | 'warning' | 'secondary';
  }>({
    show: false,
    title: '',
    message: '',
    confirmLabel: 'Confirmar',
    confirmVariant: 'primary'
  });
  const [dialogBusy, setDialogBusy] = useState(false);
  const [dialogAction, setDialogAction] = useState<null | (() => Promise<void> | void)>(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', theme);
  }, [theme]);

  const dismissToast = (id: string) => {
    setToasts((prev) => prev.filter((item) => item.id !== id));
  };

  const pushToast = (payload: Omit<AppToastItem, 'id'>) => {
    setToasts((prev) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      return [...prev, { id, ...payload }];
    });
  };

  useEffect(() => {
    if (!error) return;
    pushToast({ variant: 'danger', title: 'Erro', message: error, durationMs: 7000 });
    setError(null);
  }, [error]);

  useEffect(() => {
    if (!info) return;
    pushToast({ variant: 'success', title: 'Sucesso', message: info, durationMs: 5000 });
    setInfo(null);
  }, [info]);

  const apiBase = useMemo(() => resolveApiBase(), []);

  const loadOverview = useCallback(async () => {
    const url = apiBase ? `${apiBase}/api/job-monitor/overview` : '/api/job-monitor/overview';
    const res = await axios.get(url, { withCredentials: true });
    setOverviewQueues(Array.isArray(res.data?.queues) ? res.data.queues : []);
  }, [apiBase]);

  const loadCapacity = useCallback(async () => {
    const url = apiBase ? `${apiBase}/api/job-monitor/capacity` : '/api/job-monitor/capacity';
    const res = await axios.get(url, { withCredentials: true });
    setCapacity((res.data || null) as CapacityResponse | null);
  }, [apiBase]);

  const loadJobs = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoadingJobs(true);
    const query = new URLSearchParams();
    const columnStatusFilter = String(columnFilters.status || '').trim();
    const columnQueueFilter = String(columnFilters.queueName || '').trim();
    const columnJobFilter = String(columnFilters.jobName || '').trim();
    const columnSearchParts = Object.entries(columnFilters)
      .filter(([key, value]) => {
        if (!String(value || '').trim()) return false;
        return !['status', 'queueName'].includes(String(key));
      })
      .map(([key, value]) => `${key}:${String(value || '').trim()}`);

    const effectiveStatusFilter = columnStatusFilter || statusFilter;
    const effectiveQueueFilter = columnQueueFilter || queueFilter;
    const effectiveSearchFilter = [
      searchFilter.trim(),
      columnJobFilter,
      ...columnSearchParts,
    ].filter(Boolean).join(' ').trim();

    const hasAnyFilter = Boolean(effectiveStatusFilter || effectiveQueueFilter || effectiveSearchFilter || inFlightOnly);
    const normalizedStatus = String(effectiveStatusFilter || '').trim().toLowerCase();
    const knownStatuses = ['waiting', 'active', 'completed', 'failed', 'delayed', 'paused', 'cancelled'];
    const isExactKnownStatus = knownStatuses.includes(normalizedStatus);
    query.set('page', hasAnyFilter ? '1' : String(page));
    query.set('pageSize', hasAnyFilter ? '100' : String(pageSize));
    if (hasAnyFilter) query.set('allFiltered', 'true');
    if (effectiveStatusFilter) {
      if (isExactKnownStatus) query.set('status', normalizedStatus);
      else query.set('statusLike', effectiveStatusFilter);
    }
    if (effectiveQueueFilter) query.set('queueName', effectiveQueueFilter);
    if (effectiveSearchFilter) query.set('search', effectiveSearchFilter);
    if (inFlightOnly && !effectiveStatusFilter) query.set('inFlightOnly', 'true');

    try {
      const url = apiBase ? `${apiBase}/api/job-monitor/jobs?${query.toString()}` : `/api/job-monitor/jobs?${query.toString()}`;
      const res = await axios.get(url, { withCredentials: true });
      setJobs(Array.isArray(res.data?.items) ? res.data.items : []);
      setTotal(Number(res.data?.total || 0));
      if (hasAnyFilter && page !== 1) {
        setPage(1);
      }
    } finally {
      if (!options?.silent) setLoadingJobs(false);
    }
  }, [apiBase, columnFilters, inFlightOnly, page, pageSize, queueFilter, searchFilter, statusFilter]);

  const refreshAll = useCallback(async (options?: { silent?: boolean }) => {
    try {
      await Promise.all([loadOverview(), loadCapacity(), loadJobs(options)]);
      setError(null);
    } catch (err: any) {
      if (!options?.silent) setLoadingJobs(false);
      setError(err?.response?.data?.message || err?.message || 'Falha ao carregar jobs.');
    }
  }, [loadCapacity, loadJobs, loadOverview]);

  useEffect(() => {
    if (bootstrapped) return;
    setLoading(true);
    refreshAll()
      .finally(() => {
        setLoading(false);
        setBootstrapped(true);
      });
  }, [bootstrapped, refreshAll]);

  useEffect(() => {
    if (!bootstrapped) return;
    loadJobs();
  }, [bootstrapped, loadJobs]);

  const hasInFlightJobs = useMemo(
    () => jobs.some((job) => ['waiting', 'active', 'delayed'].includes(String(job.status || '').toLowerCase())),
    [jobs]
  );

  useEffect(() => {
    if (!bootstrapped) return;
    const intervalMs = hasInFlightJobs ? 10000 : 15000;
    const timer = setInterval(() => {
      refreshAll({ silent: true });
    }, intervalMs);
    return () => clearInterval(timer);
  }, [bootstrapped, hasInFlightJobs, refreshAll]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const workerUsage = useMemo(() => {
    const capacityWorkers = capacity?.workers;
    const workers: NonNullable<CapacityResponse['workers']> = Array.isArray(capacityWorkers) ? capacityWorkers : [];

    if (workers.length === 0) {
      return {
        sampled: false,
        workerCount: 0,
        inUse: 0,
        total: 0,
        available: 0,
      };
    }

    let inUse = 0;
    let total = 0;
    let workerCount = 0;

    for (const worker of workers) {
      const activeHandlers = Number(worker?.runtimeStats?.activeHandlers || 0);
      const maxHandlers = Number(worker?.runtimeStats?.maxActiveHandlersConfigured || 0);
      if (maxHandlers <= 0) continue;

      workerCount += 1;
      total += maxHandlers;
      inUse += Math.max(0, Math.min(activeHandlers, maxHandlers));
    }

    return {
      sampled: true,
      workerCount,
      inUse,
      total,
      available: Math.max(0, total - inUse),
    };
  }, [capacity]);

  const resolveDedicatedWorkersForJob = useCallback((job: JobExecution) => {
    const isPartitionJob = Number(job?.payload?.partitionCount || 0) > 1
      && Number.isFinite(Number(job?.payload?.partitionIndex));

    if (isPartitionJob) {
      return 1;
    }

    const queueName = String(job?.queueName || '');
    const isCompleteCoordinator = queueName === 'sap-log-analyzer-complete-jobs'
      && Number(job?.payload?.partitionCount || 0) <= 1;

    if (isCompleteCoordinator) {
      return 1;
    }

    const requested = Number(job?.payload?.parallelUserWorkers || 0);
    if (Number.isFinite(requested) && requested > 0) {
      return Math.max(1, Math.floor(requested));
    }

    return 1;
  }, []);

  const openConfirmDialog = (params: {
    title: string;
    message: string;
    confirmLabel?: string;
    confirmVariant?: 'primary' | 'danger' | 'warning' | 'secondary';
    onConfirm: () => Promise<void> | void;
  }) => {
    setDialogState({
      show: true,
      title: params.title,
      message: params.message,
      confirmLabel: params.confirmLabel || 'Confirmar',
      confirmVariant: params.confirmVariant || 'primary'
    });
    setDialogAction(() => params.onConfirm);
  };

  const closeDialog = () => {
    if (dialogBusy) return;
    setDialogState((prev) => ({ ...prev, show: false }));
    setDialogAction(null);
  };

  const runDialogConfirm = async () => {
    if (!dialogAction) return;
    setDialogBusy(true);
    try {
      await dialogAction();
      setDialogState((prev) => ({ ...prev, show: false }));
      setDialogAction(null);
    } finally {
      setDialogBusy(false);
    }
  };

  const runTestJob = async () => {
    try {
      const url = apiBase ? `${apiBase}/api/job-monitor/test-ping` : '/api/job-monitor/test-ping';
      await axios.post(
        url,
        { message: 'Execução de teste do monitor de jobs', delayMs: 1000 },
        { withCredentials: true }
      );
      setInfo('Job de teste enfileirado com sucesso.');
      refreshAll();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Falha ao enfileirar job de teste.');
    }
  };

  const cancelJob = async (id: string, force = false) => {
    try {
      const url = apiBase ? `${apiBase}/api/job-monitor/jobs/${id}/cancel` : `/api/job-monitor/jobs/${id}/cancel`;
      await axios.post(url, { force }, { withCredentials: true });
      setInfo(force ? 'Cancelamento forçado aplicado.' : 'Cancelamento solicitado.');
      refreshAll();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || (force ? 'Falha ao forçar cancelamento do job.' : 'Falha ao cancelar job.'));
    }
  };

  const retryJob = async (id: string) => {
    try {
      const url = apiBase ? `${apiBase}/api/job-monitor/jobs/${id}/retry` : `/api/job-monitor/jobs/${id}/retry`;
      await axios.post(url, {}, { withCredentials: true });
      setInfo('Job reenfileirado com sucesso.');
      refreshAll();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Falha ao reenfileirar job.');
    }
  };

  const deleteJob = async (id: string) => {
    try {
      const url = apiBase ? `${apiBase}/api/job-monitor/jobs/${id}` : `/api/job-monitor/jobs/${id}`;
      await axios.delete(url, { withCredentials: true });
      setInfo('Job removido da lista com sucesso.');
      if (detailJob?._id === id) setDetailJob(null);
      refreshAll();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Falha ao remover job da lista.');
    }
  };

  const clearJobs = async () => {
    try {
      const url = apiBase ? `${apiBase}/api/job-monitor/jobs/clear` : '/api/job-monitor/jobs/clear';
      const payload: Record<string, string> = {};
      if (queueFilter) payload.queueName = queueFilter;
      if (statusFilter) payload.status = statusFilter;
      (payload as any).clearQueueStats = true;

      const res = await axios.post(url, payload, { withCredentials: true });
      const deleted = Number(res?.data?.deleted || 0);
      const queueCompletedRemoved = Number(res?.data?.queueCompletedRemoved || 0);
      const queueFailedRemoved = Number(res?.data?.queueFailedRemoved || 0);
      const message = res?.data?.message
        || `Limpeza concluída. ${deleted} job(s) removido(s). Filas: completed=${queueCompletedRemoved}, failed=${queueFailedRemoved}.`;
      setInfo(message);
      if (detailJob && TERMINAL_JOB_STATUSES.includes(detailJob.status as any)) {
        setDetailJob(null);
      }
      refreshAll();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Falha ao limpar jobs.');
    }
  };

  const stopAndClearAllJobs = async () => {
    try {
      const url = apiBase ? `${apiBase}/api/job-monitor/jobs/stop-clear-all` : '/api/job-monitor/jobs/stop-clear-all';
      const res = await axios.post(url, {}, { withCredentials: true });
      setInfo(res?.data?.message || 'Todos os jobs foram interrompidos e limpos com sucesso.');
      setDetailJob(null);
      setPage(1);
      refreshAll();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Falha ao interromper e limpar todos os jobs.');
    }
  };

  const recoverOrphanJobs = async () => {
    try {
      const url = apiBase ? `${apiBase}/api/job-monitor/jobs/recover-orphans` : '/api/job-monitor/jobs/recover-orphans';
      const res = await axios.post(url, { autoRetry: true }, { withCredentials: true });
      setInfo(res?.data?.message || 'Recuperação de jobs órfãos concluída.');
      setPage(1);
      refreshAll();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Falha ao recuperar jobs órfãos.');
    }
  };

  const queueColumns: DataTableColumn<OverviewQueue>[] = useMemo(() => ([
    { key: 'queueName', label: 'Fila', sortable: true },
    { key: 'waiting', label: 'Waiting', sortable: false, render: (_v, row) => Number(row.counts.waiting || 0).toLocaleString('pt-BR') },
    { key: 'active', label: 'Active', sortable: false, render: (_v, row) => Number(row.counts.active || 0).toLocaleString('pt-BR') },
    { key: 'completed', label: 'Completed', sortable: false, render: (_v, row) => Number(row.counts.completed || 0).toLocaleString('pt-BR') },
    { key: 'failed', label: 'Failed', sortable: false, render: (_v, row) => Number(row.counts.failed || 0).toLocaleString('pt-BR') },
    { key: 'delayed', label: 'Delayed', sortable: false, render: (_v, row) => Number(row.counts.delayed || 0).toLocaleString('pt-BR') },
    { key: 'paused', label: 'Paused', sortable: false, render: (_v, row) => Number(row.counts.paused || 0).toLocaleString('pt-BR') }
  ]), []);

  const statusBadgeClass = (status: string) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'completed') return 'badge bg-success';
    if (normalized === 'failed') return 'badge bg-danger';
    if (normalized === 'active') return 'badge bg-primary';
    if (normalized === 'waiting' || normalized === 'delayed') return 'badge bg-warning text-dark';
    if (normalized === 'cancelled') return 'badge bg-secondary';
    return 'badge bg-light text-dark';
  };

  const consolidatedProgressByJobId = useMemo(() => {
    const result = new Map<string, { progress: number; workers: number }>();
    const partitionJobsByConsolidation = new Map<string, JobExecution[]>();

    for (const item of jobs) {
      const queueName = String(item?.queueName || '');
      if (queueName !== 'sap-log-analyzer-complete-jobs') continue;

      const consolidationId = String(item?.payload?.consolidationId || '').trim();
      const partitionCount = Number(item?.payload?.partitionCount || 0);
      const partitionIndex = Number(item?.payload?.partitionIndex);
      const isPartition = partitionCount > 1 && Number.isFinite(partitionIndex);
      if (!consolidationId || !isPartition) continue;

      const existing = partitionJobsByConsolidation.get(consolidationId) || [];
      existing.push(item);
      partitionJobsByConsolidation.set(consolidationId, existing);
    }

    for (const item of jobs) {
      const queueName = String(item?.queueName || '');
      if (queueName !== 'sap-log-analyzer-complete-jobs') continue;

      const consolidationId = String(item?.payload?.consolidationId || '').trim();
      if (!consolidationId) continue;

      const partitionJobs = partitionJobsByConsolidation.get(consolidationId) || [];
      if (partitionJobs.length === 0) continue;

      const byPartition = new Map<number, JobExecution>();
      for (const partitionJob of partitionJobs) {
        const index = Number(partitionJob?.payload?.partitionIndex);
        if (!Number.isFinite(index)) continue;

        const previous = byPartition.get(index);
        if (!previous) {
          byPartition.set(index, partitionJob);
          continue;
        }

        const prevTime = new Date(previous.createdAt || 0).getTime();
        const nextTime = new Date(partitionJob.createdAt || 0).getTime();
        if (nextTime >= prevTime) {
          byPartition.set(index, partitionJob);
        }
      }

      const partitions = Array.from(byPartition.values());
      if (partitions.length === 0) continue;

      let progressTotal = 0;
      for (const partition of partitions) {
        const status = String(partition.status || '').toLowerCase();
        if (status === 'completed') {
          progressTotal += 100;
          continue;
        }

        const current = Math.max(0, Math.min(100, Number(partition.progress || 0)));
        progressTotal += current;
      }

      const consolidated = Math.max(0, Math.min(100, progressTotal / partitions.length));
      result.set(String(item._id), {
        progress: consolidated,
        workers: partitions.length,
      });
    }

    return result;
  }, [jobs]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const jobsColumns: DataTableColumn<JobExecution>[] = useMemo(() => ([
    { key: 'queueName', label: 'Fila', sortable: true },
    { key: 'jobName', label: 'Job', sortable: true },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value: any) => <span className={statusBadgeClass(String(value || ''))}>{String(value || '-')}</span>
    },
    {
      key: 'progress',
      label: 'Progresso',
      sortable: false,
      render: (_value: any, row: JobExecution) => {
        const consolidated = consolidatedProgressByJobId.get(String(row._id));
        const current = consolidated
          ? Math.max(0, Math.min(100, Number(consolidated.progress || 0)))
          : Math.max(0, Math.min(100, Number(row.progress || 0)));
        const barWidth = current > 0 ? Math.max(current, 2) : 0;

        const isActive = String(row.status || '').toLowerCase() === 'active';
        const staleThresholdMs = 10 * 60 * 1000; // 10 minutes
        const heartbeatTs = row.lastHeartbeatAt || row.updatedAt || row.startedAt;
        const isStale = isActive && heartbeatTs
          ? (Date.now() - new Date(heartbeatTs).getTime()) > staleThresholdMs
          : false;

        return (
          <div style={{ minWidth: 180 }}>
            <div className="progress" style={{ height: 8, backgroundColor: 'var(--bs-secondary-bg-subtle, rgba(255,255,255,0.12))' }}>
              <div
                className="progress-bar"
                role="progressbar"
                style={{
                  width: `${barWidth}%`,
                  backgroundColor: isStale ? 'var(--bs-warning, #ffc107)' : 'var(--bs-primary, #0d6efd)',
                  transition: 'width 0.3s ease',
                }}
                aria-valuenow={current}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
            <div className="small text-muted mt-1">
              {current}%
              {consolidated ? ` - Consolidado (${consolidated.workers} workers)` : ''}
              {row.progressMessage ? ` - ${row.progressMessage}` : ''}
              {buildConsolidationJobDetail(row) ? ` - ${buildConsolidationJobDetail(row)}` : ''}
            </div>
            {isStale && (
              <div className="small mt-1" style={{ color: 'var(--bs-warning-text-emphasis, #997404)' }}>
                ⚠️ Sem atualização há {Math.floor((Date.now() - new Date(heartbeatTs!).getTime()) / 60000)} min — job pode estar travado
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'jobWorkers',
      label: 'Workers no job',
      sortable: false,
      render: (_value: any, row: JobExecution) => {
        const consolidated = consolidatedProgressByJobId.get(String(row._id));
        if (consolidated && consolidated.workers > 0) {
          return `${consolidated.workers} (partições)`;
        }
        const dedicated = resolveDedicatedWorkersForJob(row);
        return String(dedicated);
      }
    },
    {
      key: 'cpuJob',
      label: 'CPU job (%)',
      sortable: false,
      render: (_value: any, row: JobExecution) => {
        const realCpu = row.runtimeStats?.processCpuPct;
        const estimatedCpu = row.resourceEstimate?.estimatedCpuPct;

        if (typeof realCpu === 'number' && Number.isFinite(realCpu) && realCpu > 0.01) {
          return `${Number(realCpu).toFixed(2)} (real)`;
        }

        if (typeof estimatedCpu === 'number' && Number.isFinite(estimatedCpu)) {
          return `${Number(estimatedCpu).toFixed(2)} (estimado)`;
        }

        return '-';
      }
    },
    {
      key: 'ramJob',
      label: 'RAM job (MB)',
      sortable: false,
      render: (_value: any, row: JobExecution) => {
        const realRam = row.runtimeStats?.processRssMb;
        const estimatedRam = row.resourceEstimate?.estimatedRamMb;

        if (typeof realRam === 'number' && Number.isFinite(realRam)) {
          return `${Number(realRam).toFixed(2)} (real)`;
        }

        if (typeof estimatedRam === 'number' && Number.isFinite(estimatedRam)) {
          return `${Number(estimatedRam).toFixed(2)} (estimado)`;
        }

        return '-';
      }
    },
    {
      key: 'createdAt',
      label: 'Criado em',
      sortable: true,
      render: (value: any) => {
        if (!value) return '-';
        const dt = new Date(value);
        if (Number.isNaN(dt.getTime())) return '-';
        return dt.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
      }
    },
    {
      key: 'createdBy',
      label: 'Criado por',
      sortable: false,
      render: (_value: any, row: JobExecution) => row.createdBy?.name || row.createdBy?.id || '-'
    },
    {
      key: 'acoes',
      label: 'Ações',
      sortable: false,
      render: (_value: any, row: JobExecution) => {
        const canCancel = row.status === 'waiting' || row.status === 'delayed' || row.status === 'active';
        const canForceCancel = row.status === 'active';
        const canRetry = row.status === 'failed' || row.status === 'cancelled';
        const canDelete = TERMINAL_JOB_STATUSES.includes(row.status as any);
        return (
          <ActionMenu
            actions={[
              {
                key: 'detalhes',
                label: 'Ver detalhes',
                icon: Icons.Eye,
                onClick: () => setDetailJob(row)
              },
              {
                key: 'cancelar',
                label: 'Cancelar job',
                icon: Icons.Stop,
                variant: 'warning',
                disabled: !canCancel,
                onClick: () => openConfirmDialog({
                  title: 'Cancelar job',
                  message: 'Deseja solicitar o cancelamento deste job?',
                  confirmLabel: 'Cancelar job',
                  confirmVariant: 'warning',
                  onConfirm: () => cancelJob(row._id, false)
                })
              },
              {
                key: 'cancelar-forcado',
                label: 'Cancelar forçado',
                icon: Icons.Stop,
                variant: 'danger',
                disabled: !canForceCancel,
                onClick: () => openConfirmDialog({
                  title: 'Cancelar job forçado',
                  message: 'Deseja forçar o cancelamento deste job ativo? Use quando ele estiver travado após reinício.',
                  confirmLabel: 'Forçar cancelamento',
                  confirmVariant: 'danger',
                  onConfirm: () => cancelJob(row._id, true)
                })
              },
              {
                key: 'retry',
                label: 'Reenfileirar',
                icon: Icons.ArrowClockwise,
                variant: 'primary',
                disabled: !canRetry,
                onClick: () => openConfirmDialog({
                  title: 'Reenfileirar job',
                  message: 'Deseja reenfileirar este job?',
                  confirmLabel: 'Reenfileirar',
                  confirmVariant: 'primary',
                  onConfirm: () => retryJob(row._id)
                })
              },
              {
                key: 'delete',
                label: 'Remover da lista',
                icon: Icons.Trash,
                variant: 'danger',
                disabled: !canDelete,
                onClick: () => openConfirmDialog({
                  title: 'Remover job da lista',
                  message: 'Deseja remover este job finalizado da lista do monitor?',
                  confirmLabel: 'Remover',
                  confirmVariant: 'danger',
                  onConfirm: () => deleteJob(row._id)
                })
              }
            ]}
          />
        );
      }
    }
  ]), [consolidatedProgressByJobId, resolveDedicatedWorkersForJob, cancelJob, retryJob, deleteJob]);

  if (loading) {
    return (
      <>
        
        <Spinner />
      </>
    );
  }

  const canClearCurrentFilter = !statusFilter || TERMINAL_JOB_STATUSES.includes(statusFilter as any);

  return (
    <>
      
      <AppToastContainer toasts={toasts} onDismiss={dismissToast} />

      <div className="container-fluid py-4 px-4" style={{ marginTop: 80 }}>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <h1 className="mb-0">Monitor de Jobs</h1>
            <p className="lead mb-0">Acompanhe filas, progresso e histórico das execuções assíncronas.</p>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary" onClick={() => refreshAll()}>Atualizar</button>
            <button className="btn btn-primary" onClick={runTestJob}>Disparar job de teste</button>
          </div>
        </div>

        <div className="mb-4">
          <h5 className="mb-3">Visão das filas</h5>
          <DataTable
            columns={queueColumns}
            data={overviewQueues}
            keyExtractor={(row) => row.queueName}
            loading={false}
            striped
            hover
            responsive
            emptyMessage="Nenhuma fila encontrada."
          />
        </div>

        <div className="mb-4">
          <h5 className="mb-3">Capacidade CPU/RAM dos workers</h5>
          <div className="alert alert-info py-2 px-3 small mb-3">
            {workerUsage.sampled
              ? (
                <>
                  Slots ativos (amostrados): <strong>{Number(workerUsage.inUse || 0)}</strong>
                  {' '}| Slots livres (amostrados): <strong>{Number(workerUsage.available || 0)}</strong>
                  {' '}| Capacidade amostrada total: <strong>{Number(workerUsage.total || 0)}</strong>
                </>
              )
              : (
                <>
                  Sem telemetria de workers no momento. Os limites configurados abaixo são apenas configuração, não uso real.
                </>
              )}
          </div>
          <div className="row g-3 mb-3">
            <div className="col-md-2">
              <div className="border rounded p-3 h-100">
                <div className="text-muted small">Concorrência por processo (fila complete)</div>
                <div className="fs-4 fw-semibold">{Number(capacity?.concurrencyConfig?.sapComplete || 0)}</div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="border rounded p-3 h-100">
                <div className="text-muted small">Limite global de handlers ativos</div>
                <div className="fs-4 fw-semibold">{Number(capacity?.concurrencyConfig?.maxActiveGlobal || 0)}</div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="border rounded p-3 h-100">
                <div className="text-muted small">Slots ativos (amostrados)</div>
                <div className="fs-4 fw-semibold">{Number(workerUsage.inUse || 0)}</div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="border rounded p-3 h-100">
                <div className="text-muted small">Slots livres (amostrados)</div>
                <div className="fs-4 fw-semibold">{Number(workerUsage.available || 0)}</div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="border rounded p-3 h-100">
                <div className="text-muted small">Jobs ativos</div>
                <div className="fs-4 fw-semibold">{Array.isArray(capacity?.activeJobs) ? capacity?.activeJobs.length : 0}</div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="border rounded p-3 h-100">
                <div className="text-muted small">PIDs de worker com telemetria</div>
                <div className="fs-4 fw-semibold">{Array.isArray(capacity?.workers) ? capacity?.workers.length : 0}</div>
              </div>
            </div>
          </div>

          {Array.isArray(capacity?.workers) && capacity!.workers!.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th>PID</th>
                    <th>Fila</th>
                    <th>CPU host (%)</th>
                    <th>CPU processo (%)</th>
                    <th>RAM processo (MB)</th>
                    <th>Heap usado (%)</th>
                    <th>RAM host usada (%)</th>
                    <th>Ativos</th>
                    <th>Amostrado em</th>
                  </tr>
                </thead>
                <tbody>
                  {capacity!.workers!.map((worker) => (
                    <tr key={`${String(worker.workerPid || 'no-pid')}-${String(worker.sampledAt || '')}`}>
                      <td>{String(worker.workerPid || '-')}</td>
                      <td>{String(worker.queueName || '-')}</td>
                      <td>{worker.runtimeStats?.hostCpuPct != null ? Number(worker.runtimeStats.hostCpuPct).toFixed(2) : '-'}</td>
                      <td>{Number(worker.runtimeStats?.processCpuPct || 0).toFixed(2)}</td>
                      <td>{Number(worker.runtimeStats?.processRssMb || 0).toFixed(2)}</td>
                      <td>{Number(worker.runtimeStats?.processHeapUsedPct || 0).toFixed(2)}</td>
                      <td>{Number(worker.runtimeStats?.hostMemUsedPct || 0).toFixed(2)}</td>
                      <td>
                        {Number(worker.runtimeStats?.activeHandlers || 0)}
                        /
                        {Number(worker.runtimeStats?.maxActiveHandlersConfigured || 0)}
                      </td>
                      <td>{worker.sampledAt ? new Date(worker.sampledAt).toLocaleString('pt-BR') : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="alert alert-secondary mb-0">
              Sem amostragem de CPU/RAM no momento. Execute ao menos um job ativo para o worker publicar heartbeat com métricas.
            </div>
          )}

          {capacity?.note ? <div className="small text-muted mt-2">{capacity.note}</div> : null}
        </div>

        <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
          <h5 className="mb-0 me-2">Execuções</h5>
          <div className="form-check form-switch m-0 ms-1">
            <input
              id="inflight-only"
              className="form-check-input"
              type="checkbox"
              checked={inFlightOnly}
              onChange={(e) => { setPage(1); setInFlightOnly(e.target.checked); }}
            />
            <label className="form-check-label small" htmlFor="inflight-only">Somente ativos (global)</label>
          </div>
          <select
            className="form-select"
            style={{ maxWidth: 240 }}
            value={queueFilter}
            onChange={(e) => { setPage(1); setQueueFilter(e.target.value); }}
          >
            <option value="">Todas as filas</option>
            {overviewQueues.map((q) => (
              <option key={q.queueName} value={q.queueName}>{q.queueName}</option>
            ))}
          </select>
          <select
            className="form-select"
            style={{ maxWidth: 220 }}
            value={statusFilter}
            onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
          >
            <option value="">Todos os status</option>
            <option value="waiting">waiting</option>
            <option value="active">active</option>
            <option value="completed">completed</option>
            <option value="failed">failed</option>
            <option value="delayed">delayed</option>
            <option value="paused">paused</option>
            <option value="cancelled">cancelled</option>
          </select>
          <input
            type="text"
            className="form-control"
            style={{ maxWidth: 320 }}
            value={searchFilter}
            placeholder="Buscar em fila, job, status, erro..."
            onChange={(e) => { setPage(1); setSearchFilter(e.target.value); }}
          />
          <button
            className="btn btn-outline-danger"
            disabled={!canClearCurrentFilter}
            onClick={() => openConfirmDialog({
              title: 'Limpar jobs da lista',
              message: 'Deseja remover da lista os jobs finalizados (completed/failed/cancelled) do filtro atual?',
              confirmLabel: 'Limpar lista',
              confirmVariant: 'danger',
              onConfirm: clearJobs
            })}
            title={canClearCurrentFilter ? 'Limpar jobs finalizados do filtro atual' : 'Selecione status finalizado para limpar'}
          >
            Limpar lista
          </button>
          <button
            className="btn btn-danger"
            onClick={() => openConfirmDialog({
              title: 'Interromper e limpar todos os jobs',
              message: 'Deseja interromper todos os jobs em andamento e limpar completamente as filas e a lista do monitor? Esta ação remove também o histórico exibido.',
              confirmLabel: 'Interromper e limpar tudo',
              confirmVariant: 'danger',
              onConfirm: stopAndClearAllJobs
            })}
            title="Interromper jobs em andamento e limpar filas + monitor"
          >
            Interromper e limpar tudo
          </button>
          <button
            className="btn btn-outline-warning"
            onClick={() => openConfirmDialog({
              title: 'Recuperar ativos órfãos',
              message: 'Deseja identificar jobs ativos sem worker/heartbeat e reenfileirá-los automaticamente?',
              confirmLabel: 'Recuperar órfãos',
              confirmVariant: 'warning',
              onConfirm: recoverOrphanJobs,
            })}
            title="Marca ativos órfãos como recuperáveis e reenfileira"
          >
            Recuperar ativos órfãos
          </button>
        </div>

        <DataTable
          columns={jobsColumns}
          data={jobs}
          keyExtractor={(row) => row._id}
          onFilter={(columnKey, value) => {
            setPage(1);
            setColumnFilters((prev) => ({ ...prev, [columnKey]: value }));
          }}
          filters={columnFilters}
          loading={loadingJobs}
          striped
          hover
          responsive
          pagination={true}
          currentPage={page}
          pageSize={pageSize}
          totalItems={total}
          onPageChange={(nextPage) => setPage(nextPage)}
          onPageSizeChange={(nextPageSize) => { setPage(1); setPageSize(nextPageSize); }}
          pageSizeOptions={[10, 20, 50, 100]}
          emptyMessage="Nenhum job encontrado."
        />

        <div className="text-muted small mt-2">Total: {total.toLocaleString('pt-BR')} job(s) | Página {page} de {totalPages}</div>
      </div>

      <AppModal
        show={Boolean(detailJob)}
        title="Detalhes do job"
        onClose={() => setDetailJob(null)}
        size="lg"
        footer={(
          <button type="button" className="btn btn-secondary" onClick={() => setDetailJob(null)}>
            Fechar
          </button>
        )}
      >
        {detailJob && (
          <div className="row g-3">
            <div className="col-md-6"><strong>ID:</strong><br />{detailJob._id}</div>
            <div className="col-md-6"><strong>Bull Job ID:</strong><br />{detailJob.bullJobId || '-'}</div>
            <div className="col-md-6"><strong>Fila:</strong><br />{detailJob.queueName}</div>
            <div className="col-md-6"><strong>Job:</strong><br />{detailJob.jobName}</div>
            <div className="col-md-6"><strong>Status:</strong><br /><span className={statusBadgeClass(detailJob.status)}>{detailJob.status}</span></div>
            <div className="col-md-6"><strong>Cancelamento solicitado:</strong><br />{detailJob.cancelRequested ? 'Sim' : 'Não'}</div>
            <div className="col-md-6"><strong>Progresso:</strong><br />{Math.max(0, Math.min(100, Number(detailJob.progress || 0)))}%</div>
            <div className="col-md-6"><strong>Tentativas:</strong><br />{Number(detailJob.attemptsMade || 0)} / {Number(detailJob.maxAttempts || 0)}</div>
            <div className="col-md-6"><strong>Worker PID:</strong><br />{detailJob.workerPid || '-'}</div>
            <div className="col-md-6"><strong>Criado em:</strong><br />{detailJob.createdAt ? new Date(detailJob.createdAt).toLocaleString('pt-BR') : '-'}</div>
            <div className="col-md-6"><strong>Iniciado em:</strong><br />{detailJob.startedAt ? new Date(detailJob.startedAt).toLocaleString('pt-BR') : '-'}</div>
            <div className="col-md-6"><strong>Finalizado em:</strong><br />{detailJob.finishedAt ? new Date(detailJob.finishedAt).toLocaleString('pt-BR') : '-'}</div>
            <div className="col-md-6"><strong>Criado por:</strong><br />{detailJob.createdBy?.name || detailJob.createdBy?.id || '-'}</div>
            <div className="col-md-6"><strong>CPU estimada do job (%):</strong><br />{Number(detailJob.resourceEstimate?.estimatedCpuPct || 0).toFixed(2)}</div>
            <div className="col-md-6"><strong>RAM estimada do job (MB):</strong><br />{Number(detailJob.resourceEstimate?.estimatedRamMb || 0).toFixed(2)}</div>
            <div className="col-12"><strong>Mensagem de progresso:</strong><br />{detailJob.progressMessage || '-'}</div>
            <div className="col-12"><strong>Erro:</strong><pre className="bg-body-tertiary border rounded p-2 mb-0">{detailJob.errorMessage || '-'}</pre></div>
            <div className="col-12"><strong>Payload:</strong><pre className="bg-body-tertiary border rounded p-2 mb-0">{JSON.stringify(detailJob.payload || {}, null, 2)}</pre></div>
            <div className="col-12"><strong>Resultado:</strong><pre className="bg-body-tertiary border rounded p-2 mb-0">{JSON.stringify(detailJob.result || {}, null, 2)}</pre></div>
          </div>
        )}
      </AppModal>

      <AlertConfirmModal
        show={dialogState.show}
        mode="confirm"
        title={dialogState.title}
        message={dialogState.message}
        onClose={closeDialog}
        onConfirm={runDialogConfirm}
        confirmLabel={dialogState.confirmLabel}
        confirmVariant={dialogState.confirmVariant}
        busy={dialogBusy}
      />
    </>
  );
};

export default JobMonitor;
